/**
 * PokemonCard — uses AnimatePresence back/front swap instead of CSS 3D flip.
 * 3D flip (backface-visibility: hidden) is unreliable on mobile Safari + framer-motion.
 */

import { useRef, useState, useCallback, useEffect } from "react";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import type { LiveCard, CardRarity } from "../hooks/usePokemonTCG";
import "./PokemonCard.scss";

export const RARITY_LABEL: Record<CardRarity, string> = {
  common:   "Common",
  uncommon: "Uncommon",
  rare:     "Rare",
  holo:     "Holo Rare",
  ultra:    "Ultra Rare",
  graded:   "Graded",
};

export const RARITY_COLOR: Record<CardRarity, string> = {
  common:   "#9ca3af",
  uncommon: "#34d399",
  rare:     "#60a5fa",
  holo:     "#fbbf24",
  ultra:    "#c084fc",
  graded:   "#ffd700",
};

export const RARITY_GLOW: Record<CardRarity, string> = {
  common:   "none",
  uncommon: "0 0 14px #34d39944",
  rare:     "0 0 20px #60a5fa66",
  holo:     "0 0 28px #fbbf2488",
  ultra:    "0 0 40px #c084fcaa",
  graded:   "0 0 50px #ffd700cc",
};

interface Props {
  card:       LiveCard;
  index:      number;
  faceDown?:  boolean;
  revealed?:  boolean;
  size?:      "sm" | "md" | "lg";
  onClick?:   (card: LiveCard) => void;
  className?: string;
}

export function PokemonCard({
  card, index,
  faceDown  = false,
  revealed  = true,
  size      = "md",
  onClick,
  className = "",
}: Props) {
  // showFront: true if we don't need faceDown mode, or if already revealed
  const [showFront, setShowFront] = useState(!faceDown || revealed);
  const [imageLoaded, setImageLoaded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Sync when parent passes revealed=true
  useEffect(() => {
    if (revealed) setShowFront(true);
  }, [revealed]);

  // ── 3D tilt (front face only) ─────────────────────────────────────────────
  const rawX = useMotionValue(0);
  const rawY = useMotionValue(0);
  const springCfg = { stiffness: 200, damping: 25 };
  const springX = useSpring(rawX, springCfg);
  const springY = useSpring(rawY, springCfg);
  const tiltY       = useTransform(springX, [-0.5, 0.5], [-18, 18]);
  const tiltX       = useTransform(springY, [-0.5, 0.5], [14, -14]);
  const glareX      = useTransform(springX, [-0.5, 0.5], ["0%", "100%"]);
  const glareY      = useTransform(springY, [-0.5, 0.5], ["0%", "100%"]);
  const holoAngle   = useTransform(springX, [-0.5, 0.5], ["95deg", "155deg"]);
  const holoIntensity = useTransform(
    [springX, springY],
    ([x, y]: number[]) => Math.min(1.0, 0.2 + Math.sqrt((x as number) ** 2 + (y as number) ** 2) * 2)
  );
  const glareBackground = useTransform(
    [springX, springY],
    ([x, y]: number[]) =>
      `radial-gradient(circle at ${((x as number) + 0.5) * 100}% ${((y as number) + 0.5) * 100}%, rgba(255,255,255,0.18) 0%, transparent 55%)`
  );

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!ref.current || !showFront) return;
    const rect = ref.current.getBoundingClientRect();
    rawX.set((e.clientX - rect.left) / rect.width - 0.5);
    rawY.set((e.clientY - rect.top)  / rect.height - 0.5);
  }, [rawX, rawY, showFront]);

  const handleMouseLeave = useCallback(() => { rawX.set(0); rawY.set(0); }, [rawX, rawY]);

  const handleClick = useCallback(() => {
    if (!showFront) setShowFront(true);
    if (onClick) onClick(card);
  }, [showFront, card, onClick]);

  const isSpecial = card.rarity === "ultra" || card.rarity === "graded";
  const isHolo    = card.rarity === "holo"  || isSpecial;
  const delay     = Math.min(index * 0.055, 0.4);

  return (
    <motion.div
      ref={ref}
      className={`PokemonCard PokemonCard--${size} PokemonCard--${card.rarity} ${!showFront ? "PokemonCard--facedown" : ""} ${className}`}
      initial={{ opacity: 0, y: 30, scale: 0.85 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.45, delay, ease: [0.34, 1.56, 0.64, 1] }}
      onClick={handleClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: !showFront ? "pointer" : "default", perspective: 800 }}
    >
      {/* Rainbow ring for ultra/graded */}
      {isSpecial && showFront && (
        <motion.div
          className="PokemonCard__ring"
          animate={{ rotate: 360 }}
          transition={{ duration: card.rarity === "graded" ? 2 : 4, repeat: Infinity, ease: "linear" }}
        />
      )}

      {/* Back/front swap — no CSS 3D flip */}
      <AnimatePresence initial={false}>
        {!showFront && (
          <motion.div key="back"
            className="PokemonCard__face PokemonCard__back"
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            exit={{ scaleX: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeInOut" }}
          >
            <div className="PokemonCard__back-logo" />
            <div className="PokemonCard__back-pattern" />
            {faceDown && (
              <motion.div
                className="PokemonCard__tap-hint"
                animate={{ opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                Tap to reveal
              </motion.div>
            )}
          </motion.div>
        )}

        {showFront && (
          <motion.div key="front"
            className="PokemonCard__face PokemonCard__front"
            initial={{ scaleX: 0, opacity: 0 }}
            animate={{ scaleX: 1, opacity: 1 }}
            exit={{ scaleX: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            style={{ rotateX: tiltX, rotateY: tiltY }}
          >
            {/* Card image */}
            <div className="PokemonCard__img-wrap">
              {!imageLoaded && <div className="PokemonCard__skeleton" />}
              <img
                className={`PokemonCard__img${imageLoaded ? " loaded" : ""}`}
                src={size === "lg" ? (card.imageLarge ?? card.imageSmall) : card.imageSmall}
                alt={card.name}
                onLoad={() => setImageLoaded(true)}
                onError={(e) => {
                  const img = e.target as HTMLImageElement;
                  if (size === "lg" && card.imageSmall && img.src !== card.imageSmall) {
                    img.src = card.imageSmall;
                  } else {
                    img.src = `https://placehold.co/245x342/12132a/818cf8?text=${encodeURIComponent(card.name)}`;
                    setImageLoaded(true);
                  }
                }}
              />
            </div>

            {/* Holographic overlay */}
            {isHolo && (
              <motion.div
                className="PokemonCard__holo"
                style={{
                  "--holo-angle": holoAngle,
                  "--holo-intensity": holoIntensity,
                  background: `radial-gradient(circle at ${glareX} ${glareY}, rgba(255,255,255,0.22) 0%, transparent 55%)`,
                } as React.CSSProperties}
              />
            )}
            {isSpecial && <div className="PokemonCard__shimmer" />}
            {isSpecial && <div className="PokemonCard__sparkles" />}

            {/* Glare */}
            <motion.div
              className="PokemonCard__glare"
              style={{ background: glareBackground }}
            />

            {/* Rarity badge */}
            <div className="PokemonCard__rarity-badge" style={{ color: RARITY_COLOR[card.rarity] }}>
              {card.rarity === "graded" && card.gradingCompany && (
                <span className="PokemonCard__grading-co">{card.gradingCompany} </span>
              )}
              {card.rarity === "graded" && card.grade != null
                ? <span className="PokemonCard__grade">{card.grade}</span>
                : RARITY_LABEL[card.rarity]
              }
            </div>

            {/* Value */}
            <div className="PokemonCard__value">
              ${card.approxValueUsd >= 1000
                ? (card.approxValueUsd / 1000).toFixed(1) + "k"
                : card.approxValueUsd.toLocaleString()}
            </div>

            {card.isGraded && card.grade != null && (
              <div className="PokemonCard__graded-banner">{card.gradingCompany} {card.grade}</div>
            )}
            {card.isAltArt && !card.isGraded && (
              <div className="PokemonCard__alt-tag">Alt Art</div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Name tooltip */}
      <AnimatePresence>
        {showFront && (
          <motion.div
            className="PokemonCard__tooltip"
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <span className="PokemonCard__tooltip-name">{card.name}</span>
            <span className="PokemonCard__tooltip-set">{card.setName}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
